import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { Button, Modal, absorbEvent, Input, Row, Status, Table, noop, useWindowContext } from '@iqueue/ui-kit'
import { prop } from 'ramda'
import PaginatedPage from '../../components/PaginatedPage'
import TableView from '../../components/TableView'
import SensorUpsertForm from './SensorUpsertForm'
import SensorPresenter from '../../api/sensorPresenter'
import BasicDateRenderer from '../../utils/renderers'
import usePermissions from '../../components/usePermissions'
import PickSensorStatus from '../../components/picker/PickSensorStatus'
import SnapshotPresenter from '../../api/snapshotPresenter'
import useSocket from '../../hooks/useSocket'
import EventPresenter from '../../api/eventPresenter'
import ViewStatusNodes from '../../components/ViewStatusNodes'
import useTranslate, { t } from '../../hooks/useTranslate'
import DataModal from '../../components/DataModal'
import Tooltip from '../../components/Tooltip'

export function DevEUIFormatter({ devEUI }) {
  const formattedDevEUI = devEUI.match(/.{1,2}/g).join(' ')
  return <b>{ formattedDevEUI.toUpperCase() }</b>
}

function DiffChangesDisplay({ items }) {
  const schema = useMemo(() => {
    return [{
      key: 'limits',
      title: t('sensor.diff.limit'),
      render: (_, item) => {
        return <div className="limits-cell">
          <div className="previous-value">
            <p>{ t('sensor.diff.prevMin') } { item?.diff?.limits?.min ?? 'N/A' }</p>
            <p>{ t('sensor.diff.prevMax') } { item?.diff?.limits?.max ?? 'N/A' }</p>
          </div>
          <div className="separator"> →</div>
          <div className="current-value">
            <p>{ t('sensor.diff.min') } { item?.entity?.limits?.min ?? 'N/A' }</p>
            <p>{ t('sensor.diff.max') } { item?.entity?.limits?.max ?? 'N/A' }</p>
          </div>
        </div>
      },
    }, {
      key: 'status',
      title: t('sensor.diff.status'),
      render: (_, item) => {
        return <div className="status-cell">
          <div className="previous-value">
            { item?.diff?.status ? <Status
              style={ { marginTop: '0.8rem' } }
              inline={ item?.diff?.status !== 'NO_SIGNAL' }
              weak
              warning={ item?.diff?.status === 'TECH_WORKS' }
              error={ item?.diff?.status === 'NO_SIGNAL' || item?.diff?.status === 'SHORT_CIRCUIT' || item?.diff?.status === 'NO_SENSOR_SIGNAL' || item?.diff?.status === 'UNKNOWN_UNIT' || item?.diff?.status === 'INVALID_DRIVER' }
            >
              { t(`status.sensor.${ item?.diff?.status }`) }
            </Status> : <span>N/A</span> }
          </div>
          <div className="separator"> →</div>
          <div className="current-value">
            <Status
              style={ { marginTop: '0.8rem' } }
              inline={ item?.entity?.status !== 'NO_SIGNAL' }
              weak
              warning={ item?.entity?.status === 'TECH_WORKS' }
              error={ item?.entity?.status === 'NO_SIGNAL' || item?.entity?.status === 'SHORT_CIRCUIT' || item?.entity?.status === 'NO_SENSOR_SIGNAL' || item?.entity?.status === 'UNKNOWN_UNIT' || item?.entity?.status === 'INVALID_DRIVER' }
            >
              { t(`status.sensor.${ item?.entity?.status }`) }
            </Status>
          </div>
        </div>
      },
    }, {
      key: 'thresholds',
      title: t('sensor.diff.thresholds'),
      render: (_, item) => {
        return <div className="thresholds-cell">
          <div className="previous-value">
            <div>{ t('sensor.diff.prevWarn') } { item?.diff?.thresholds?.warn?.map(range => `[${ range[0] }, ${ range[1] }]`).join(', ') ?? 'N/A' }</div>
            <div>{ t('sensor.diff.prevCritical') } { item?.diff?.thresholds?.critical?.map(range => `[${ range[0] }, ${ range[1] }]`).join(', ') ?? 'N/A' }</div>
          </div>
          <div className="separator"> →</div>
          <div className="current-value">
            <div>{ t('sensor.diff.warn') } { item?.entity?.thresholds?.warn?.map(range => `[${ range[0] }, ${ range[1] }]`).join(', ') ?? 'N/A' }</div>
            <div>{ t('sensor.diff.critical') } { item?.entity?.thresholds?.critical?.map(range => `[${ range[0] }, ${ range[1] }]`).join(', ') ?? 'N/A' }</div>
          </div>
        </div>
      },
    }, {
      key: 'updatedAt',
      title: t('sensor.diff.updatedAt'),
      render: (_, item) => {
        return <div className="updatedAt-cell">
          <span className="previous-value">{ BasicDateRenderer(item?.diff?.updatedAt) || 'N/A' }</span>
          <span className="separator"> → </span>
          <span className="current-value">{ BasicDateRenderer(item?.entity?.updatedAt) }</span>
        </div>
      },
    }]
  }, [])

  return <>
    <Table
      indexable
      entries={ items }
      schema={ schema }
      minWidth="200rem"
      filter={ {} }
    />
  </>
}

function HistorySensor({ items }) {
  const schema = useMemo(() => {
    return [{
      key: 'value',
      title: t('sensor.history.value'),
      align: "center",
      render: (v) => <b>{ v.join(', ') }</b>,
    },
      {
        key: 'voltage',
        title: t('sensor.history.voltage'),
        width: '15rem',
        align: 'center',
        render: (v) => <b>{ `${ (v / 1000).toFixed(2) } V` }</b>,
      },
      {
        key: 'type',
        title: t('sensor.history.type'),
        width: '15rem',
        render: (v) => t(`type.sensor.${ v }`),
      },
      {
        key: 'nodeStatus',
        title: t('sensor.history.nodeStatus'),
        width: '25rem',
        align: 'center',
        render: (_, item) => <ViewStatusNodes item={ item }/>,
      },
      {
        key: 'sensorStatus',
        title: t('sensor.history.sensorStatus'),
        width: '20rem',
        render: (v) => (
          <Status
            style={ {
              marginTop: '0.8rem',
              width: '100%',
            } }
            inline={ v !== 'NO_SIGNAL' }
            weak
            warning={ v === 'TECH_WORKS' }
            error={ v === 'NO_SIGNAL' || v === 'SHORT_CIRCUIT' || v === 'NO_SENSOR_SIGNAL' || v === 'UNKNOWN_UNIT' || v === 'INVALID_DRIVER' }
          >
            { t(`status.sensor.${ v }`) }
          </Status>
        ),
      },
      {
        key: 'createdAt',
        title: t('sensor.history.at'),
        width: '18rem',
        // render: BasicDateRenderer,
      }]
  }, [])

  return <Table
    indexable
    schema={ schema }
    entries={ items || [] }
    minWidth={ '110rem' }
  />
}

export function SensorsTable({ ...props }) {
  const socket = useSocket()
  const { permissions } = usePermissions()
  const locale = useTranslate()

  const [exitInterval, setExitInterval] = useState(false)
  const [editStatus, setEditStatus] = useState(null)
  const [packageOpen, setPackagesOpen] = useState(false)
  const [currentSensor, setCurrentSensor] = useState({})
  const [responsePackets, setResponsePackets] = useState([])
  const [historyDialog, setHistoryDialog] = useState(false)
  const [historySensor, setHistorySensor] = useState([])
  const [entries, setEntries] = useState(props?.data?.entries || [])
  const [fieldTypes, setFieldTypes] = useState(null)
  const [formModal, setFormModal] = useState(false)
  const allowedStatuses = new Set(["WORKING", "NO_SIGNAL", "SHORT_CIRCUIT", "TECH_WORKS", "NO_SENSOR_SIGNAL"])

  const schema = useMemo(() => ([
    {
      key: 'devEUI',
      title: t('sensor.table.devEUI'),
      // width:'19rem',
      render: (v) => <span data-tooltip="table">
        <DevEUIFormatter devEUI={ v }/>
      </span>,
      filter: (value, item) => item.devEUI.toLocaleLowerCase().includes(value.toLocaleLowerCase()),
    },
    {
      key: 'node',
      title: t('sensor.table.node'),
      render: prop('name'),
      width: '20rem',
    },
    {
      key: 'serialNumber',
      title: t('sensor.table.serialNumber'),
      render: (v) => v,
      width: '20rem',
    },
    {
      key: 'model',
      title: t('sensor.table.model'),
      render: prop('name'),
      width: '15rem',
    }, {
      key: 'id',
      width: '10rem',
      title: t('sensor.table.exitInterval'),
      render: (_, item) => <>
        <b>{ item.state?.interval ? `${ Math.floor(item.state?.interval / 60) } мин` : '' }</b>
      </>,
    }, {
      key: 'type',
      title: t('sensor.table.type'),
      render: (v) => t(`type.sensor.${ v }`),
      width: '20rem',
    },
    {
      key: 'status',
      title: t('sensor.table.status'),
      width: '25rem',
      render: (v, item) => {
        return <>
          <span>
          <Status
            style={ {
              marginTop: '0.8rem',
              width: '100%',
            } }
            inline={ v !== 'NO_SIGNAL' }
            weak
            warning={ v === 'TECH_WORKS' }
            error={ v === 'NO_SIGNAL' || v === 'SHORT_CIRCUIT' || v === 'NO_SENSOR_SIGNAL' || v === 'UNKNOWN_UNIT' || v === 'INVALID_DRIVER' }
          >
        { t(`status.sensor.${ v }`) }
      </Status>
      </span>
          { permissions.SENSOR?.includes('UPDATE_STATUS') && <Button
            onClick={ (e) => {
              absorbEvent(e)
              setEditStatus(item)
              setExitInterval(true)
            } }
            primary
            icon={ 'edit' }
          /> }
        </>
      },
    },
    {
      key: 'createdAt',
      title: t('sensor.table.createdAt'),
      render: BasicDateRenderer,
      width: '18rem',
    },
    {
      key: 'packages',
      width: '16rem',
      style: {
        position: 'sticky',
        right: 0,
      },
      render: (_, item) => <Row justify={ 'space-between' }>
        <Tooltip label={ t('sensor.modal.sensorEvents') }>
          <Button
            data-tooltip="overview"
            onClick={ (e) => {
              e.stopPropagation()
              setCurrentSensor(item)
              setHistoryDialog(true)
              getHistorySensor(item, 1, 50).catch(console.log)
            } }
            secondary
            icon={ 'overview' }
          />
        </Tooltip>

        <Tooltip label={ 'История датчика' }>
          <Button
            data-tooltip="history"
            onClick={ (e) => handleGetSensorHistory(e, item) }
            secondary
            icon="history"
          />
        </Tooltip>

        <Tooltip label={ t('sensorsSendCommand.parameters') }>
          <Button
            data-tooltip="settings"
            onClick={ (e) => handleChangeForm(e, item) }
            primary
            icon="deployed_code"
          />
        </Tooltip>

        { permissions.SENSOR?.includes('UPDATE_STATUS') && (
          <Tooltip label={ t('sensor.modal.packageInfo') }>
            <Button
              data-tooltip="packets"
              onClick={ (e) => {
                e.stopPropagation()
                handlePacketClick(e, item)
              } }
              secondary
              icon="visibility"
            />
          </Tooltip>
        ) }
      </Row>,
    },
  ]), [permissions, locale])

  const responseSchema = useMemo(() => [
    {
      key: 'sensorId',
      title: 'devEUI',
      render: () => <DevEUIFormatter devEUI={ currentSensor.devEUI }/>,
    }, {
      key: 'command',
      title: 'Команда',
      width: '20rem',
      render: (v, item) => {
        if (item.command) {
          const commandKey = Object.keys(item.command)[0]
          const commandValue = item.command[commandKey]

          return `${ commandKey }: (${ Math.floor(commandValue / 60) } мин)`
        }
        return 'N/A'
      },
    }, {
      key: 'data',
      title: 'Данные (HEX)',
      width: '15rem',
      render: (v) => <DevEUIFormatter devEUI={ v }/>,
    }, {
      key: 'status',
      title: 'Статус',
      width: '15rem',
      render: (v) => {
        return <Status
          style={ { marginTop: '0.8rem' } }
          inline={ v !== 'ERROR' }
          weak={ v !== 'ERROR' }
          accent={ v === 'PENDING' }
          warning={ v === 'ABANDONED' }
          error={ v === 'ERROR' }
        >
          { t(`sensor.status.${ v }`) }
        </Status>
      },
    }, {
      key: 'createdAt',
      title: 'Дата создания',
      width: '18rem',
      render: BasicDateRenderer,
    }, {
      key: 'updatedAt',
      title: 'Дата обновления',
      width: '18rem',
      render: BasicDateRenderer,
    }], [currentSensor, locale])

  const handlePacketClick = useCallback((e, item) => {
    absorbEvent(e)
    setCurrentSensor(item)
    SensorPresenter.get(`${ item.id }/packets`).then((item) => {
      setResponsePackets(item)
    })
    setPackagesOpen(true)
  }, [])

  const handleGetSensorHistory = useCallback((e, item) => {
    absorbEvent(e)
    Modal.show({
      fullscreen: true,
      title: 'История датчика',
      content: () => SnapshotPresenter.get(`sensors/${ item.id }`),
      children: (items) => <DiffChangesDisplay items={ items } t={ t }/>,
    }).catch(noop)
  }, [])

  const getHistorySensor = useCallback(async (item) => {
    await EventPresenter.get(`history/${ item.id || currentSensor.id }?page=${ 1 }&limit=${ 500 }`).then(setHistorySensor)
  }, [currentSensor?.id])

  const handleChangeForm = useCallback(async (e, item) => {
    e.stopPropagation()
    // const res = await HelpersPresenter.fieldTypes()
    setFormModal(true)
    setFieldTypes(item?.model?.commandsSchemaDefinition)
    setCurrentSensor(item)
  }, [])

  const messageSensorStatus = useCallback((message) => {
    if (!allowedStatuses.has(message?.context?.body)) return

    const items = props?.data?.entries.forEach((entry) => {
      if (entry.id === message.sensorId) {
        entry.status = message?.context?.body
      }
    })

    setEntries(items)
  }, [])

  useEffect(() => {
    socket.on('notices/new', messageSensorStatus)
    return () => {
      socket.off('notices/new', messageSensorStatus)
    }
  }, [socket, messageSensorStatus])

  return <>
    <TableView
      permissions={ permissions[SensorPresenter.key.toUpperCase()] }
      form={ SensorUpsertForm }
      data={ entries }
      schema={ schema }
      entity={ 'sensors' }
      minWidth={ '167rem' }
      uploadItems={ true }
      { ...props }
    />

    <Modal
      onApply={ async (item) => {
        if (!editStatus) {
          const num = { interval: item.interval * 60 }
          await SensorPresenter.interval(currentSensor.id, num)
          props?.reload()
        } else {
          await SensorPresenter.patch(`update-status/${ editStatus.id }`, { status: item.status })
          props?.reload()
        }
      } }
      title={ !editStatus ? t('sensor.modal.configureInterval') : t('sensor.modal.changeStatus') }
      isOpened={ exitInterval }
      onClose={ () => {
        setCurrentSensor({})
        setEditStatus(null)
        setExitInterval(false)
      } }
      footerActions={ [
        { key: 'cancel', title: t('modal.editWellStatusModal.close'), danger: true },
        {
          key: 'apply',
          title: !editStatus ? t('crudTableView.create') : t('crudTableView.update'),
          submit: true,
          primary: true,
        },
      ] }
    >
      { !editStatus ? <Row>
        <Input
          size={ 10 }
          name={ 'interval' }
          type={ 'number' }
          placeholder={ t('sensor.table.intervalPlaceholder') }
          required
          value={ Math.floor(currentSensor?.state?.interval / 60) || '' }
        />
        <Input
          size={ 2 }
          // disabled
          value={ 'Мин' }
        />
      </Row> : <Row>
        <PickSensorStatus
          name={ 'status' }
          value={ editStatus.status }
          placeholder={ t('sensor.table.status') }
          size={ 12 }
        />
      </Row> }
    </Modal>

    <Modal
      title={ t('sensor.modal.packageInfo') }
      isOpened={ packageOpen }
      onClose={ () => setPackagesOpen(false) }
      footerActions={ [{ key: 'cancel', title: t('modal.editWellStatusModal.close'), danger: true }] }
    >

      { responsePackets.length !== 0 ? <Table
        indexable
        minWidth={ '110rem' }
        entries={ responsePackets }
        schema={ responseSchema }
      >

      </Table> : <div className={ 'not-response' }>
        <h3>{ t('modal.editWellStatusModal.close') }</h3>
      </div> }
    </Modal>

    <Modal
      title={ t('sensor.modal.sensorEvents') }
      isOpened={ historyDialog }
      fullscreen
      onClose={ () => {
        setHistoryDialog(false)
        setHistorySensor([])
        setCurrentSensor({})
      } }
    >

      <HistorySensor
        items={ historySensor }
        onFetch={ getHistorySensor }
        currentSensorId={ currentSensor.id }
      />
    </Modal>

    <DataModal
      formModal={ formModal }
      closeModal={ setFormModal }
      data={ fieldTypes }
      currentSensorId={ currentSensor.id }
    />
  </>
}

export default function SensorsPage() {
  const locale = useTranslate()
  const { setTitle, togglePaddings, toggleScrollbars } = useWindowContext()
  const translatedTitle = useMemo(() => t('menu.sensors'), [locale])

  useEffect(() => {
    setTitle(translatedTitle)
    togglePaddings(false)
    toggleScrollbars(true)
  }, [translatedTitle])

  return <PaginatedPage
    presenter={ SensorPresenter }
    component={ SensorsTable }
  />
}
